<template>
  <v-card class="rounded-xl" :border="false" :elevation="0">
    <v-card-title class="d-flex justify-center align-center">
      <h1 class="title">{{ $t('COMMENTARY_REPAIR_FILE.TITLE') }}</h1>
      <button v-if="!openComment" @click="openComment = !openComment" class="add-comment">
        <img alt="cross-svg" class="filter" src="@/assets/icons/file/plus.svg" />
      </button>
    </v-card-title>

    <v-card-item v-if="openComment">
      <v-row>
        <v-form
          ref="commentForm"
          v-model="commentFormValid"
          @submit.prevent="saveCommentary()"
          class="w-100"
        >
          <v-col cols="12" class="d-flex justify-center pb-0">
            <v-textarea
              v-model="commentary"
              :placeholder="$t('COMMENTARY_REPAIR_FILE.PLACEHOLDER.COMMENTARY')"
              required
              :rules="[...requiredRules, ...trimmedRequiredRules]"
              rows="1"
              hide-details
              maxlength="150"
            />
          </v-col>
          <v-col cols="12">
            <v-row>
              <v-col class="d-flex justify-center" cols="6">
                <v-btn
                  class="button w-100"
                  :loading="loading"
                  variant="outlined"
                  @click="cancelCommentary"
                >
                  {{ $t('COMMON.BUTTON.CANCEL') }}
                </v-btn>
              </v-col>
              <v-col class="d-flex justify-center" cols="6">
                <v-btn
                  class="button w-100"
                  :loading="loading"
                  variant="outlined"
                  :disabled="!commentFormValid || loading"
                  type="submit"
                >
                  {{ $t('COMMON.BUTTON.VALIDATE') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-form>
      </v-row>
    </v-card-item>

    <v-infinite-scroll
      height="350"
      :items="commentary_list"
      :onLoad="load"
      @scroll="onScroll"
      empty-text=""
    >
      <template v-for="commentary in commentary_list" :key="commentary">
        <div class="mb-4 d-flex flex-row align-center ga-2">
            <v-avatar
              color="#165039"
              class="font-weight-bold"
              size="44"
              :text="commentary.author_firstname[0] + '.' + commentary.author_lastname[0]"
            />
          <section class="d-flex flex-column justify-center ga-1">
            <div class="grey-text d-flex justify-start ga-2">
              <p>{{ getFormattedDate(commentary.created_at) }}</p>
              <p>|</p>
              <p>
                {{
                  commentary.author_firstname +
                  ' ' +
                  commentary.author_lastname[0].toUpperCase() +
                  '.'
                }}
              </p>
            </div>
            <p class="comment-content">{{ commentary.content }}</p>
          </section>
        </div>
      </template>
    </v-infinite-scroll>
  </v-card>
</template>

<script>
import repairFileService from '@/services/RepairFileService';
import { auth as $store } from '@/store/AuthModule';
import { RepairFile } from '@/models/RepairFile';
import Validator from '@/components/utils/validator'
import { handleResponseError } from '@/components/utils/globalFunction'

export default {
  name: 'CommentaryRepairFile',
  props: {
    currentRepairFile: RepairFile
  },
  data() {
    return {
      commentFormValid: false,
      loading: false,
      openComment: false,
      loadingMore: false,
      page: 1,
      pages: null,
      limit: 5,

      commentary_list: [],
      commentary: '',
      requiredRules: Validator.requiredRules,
      trimmedRequiredRules: Validator.trimmedRequiredRules
    }
  },
  created() {
    this.getCommentList()
  },
  watch: {
    currentRepairFile() {
      this.getCommentList()
    }
  },
  methods: {
    async getCommentList() {
      this.loading = true
      try {
        this.page = 1
        const response = await repairFileService.getPaginatedRepairFileCommentList(
          this.currentRepairFile.boomerang_id,
          this.page,
          this.limit
        )
        this.commentary_list = response.data
        this.pages = response.max_page
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    },
    load({ done }) {
      if (this.page === this.pages) {
        done('empty')
        return
      }
      setTimeout(async () => {
        done(await this.loadMore())
      }, 250)
    },
    async onScroll(event) {
      const scrollContainer = event.target
      const scrollHeight = scrollContainer.scrollHeight
      const clientHeight = scrollContainer.clientHeight
      if (!this.loadingMore && scrollContainer.scrollTop + clientHeight >= 0.8 * scrollHeight) {
        this.loadingMore = true
        await this.loadMore()
        this.loadingMore = false
      }
    },
    async loadMore() {
      if (this.page < this.pages) {
        this.page++
        const response = await repairFileService.getPaginatedRepairFileCommentList(
          this.currentRepairFile.boomerang_id,
          this.page,
          this.limit
        )
        this.commentary_list.push(...response.data)
        return 'ok'
      } else {
        return 'empty'
      }
    },
    getFormattedDate(date) {
      return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString()
    },
    cancelCommentary() {
      this.openComment = false
      this.commentary = ''
    },
    getCurrentCollaborator() {
      return $store.state.currentCollaborator
    },
    async saveCommentary() {
      this.loading = true
      try {
        const data = {
          repair_file_boomerang_id: this.currentRepairFile.boomerang_id,
          content: this.commentary,
          requestor_collaborator_id: this.getCurrentCollaborator().technical_id
        }
        await repairFileService.createRepairFileComment(data)
        await this.getCommentList()
        this.openComment = false
        this.commentary = ''
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.v-card {
  padding: 20px;
}

.title {
  font-size: 18px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
}

.comment-row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  flex: 0 1 auto;

  .logo {
    align-self: center;
  }
}

.grey-text {
  display: flex;
  align-items: center;
  color: #acacac;
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.comment-content {
  color: black;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  white-space: pre-line;
}

.add-comment {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 25px;
  height: 25px;
  border-radius: 50px;
  background: #000;
  margin-left: 80%;
}

.filter {
  filter: invert(100%) sepia(0%) saturate(0%) hue-rotate(9deg) brightness(103%) contrast(101%);
}

.button {
  display: flex;
  padding: 10px 20px;
  justify-content: center;
  align-items: center;
  text-transform: none !important;
  border-radius: 20px;
  border: 1px solid #000;
  background: #fff;
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
</style>
